// src/pages/doctor/DoctorRecord.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { translateMedicineName } from '../../utils/medTranslations';

interface PricePlan {
  id: number;
  plan_type: 'single_purchase' | 'subscription';
  /** 订阅月数（subscription 时 > 0；单品为 0） */
  duration_months: number;
  price_tax_excluded: number;
  /** 含税单月价格（订阅视为月额，单品视为单价） */
  price_tax_included: number;
  quantity_units?: number | null;
  coverage_months_min?: number | null;
  coverage_months_max?: number | null;
  /** 计费单位：'month'（按月）或 'quantity'（按数量） */
  billing_unit?: 'month' | 'quantity';
}

interface Medicine {
  id: number;
  name: string;
  /** 'single' | 'set'（仅展示用途，不影响计算） */
  type: string;
  note?: string;
  service_type: string;
  price_plans: PricePlan[];
}

/** 选中的药品 + 价格方案 + 月数（展示/提交用：订阅固定为 duration_months） */
type SelectedPlan = { medicineId: number; pricePlanId: number; months: number };

// === セッション状態のキー（DoctorLayout と統一） ===
const SS_KEY = 'doctor.lastSearchByPath';

// === 指定パスの検索クエリを取得（なければ空文字） ===
const getSavedSearchForPath = (path: string): string => {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return '';
    const map = JSON.parse(raw) as Record<string, string>;
    return map[path] || '';
  } catch {
    return '';
  }
};

interface DoctorRecordProps {
  // 埋め込みモード時に渡す予約 ID（useParams を上書き）
  embeddedId?: number;
  // true の場合、ヘッダー（戻るボタン・タイトル）を非表示にし保存後のページ遷移を行わない
  embedded?: boolean;
  // AI提案からの自動補完（diagnosis ← AP, notes ← S）
  prefill?: { s: string; ap: string } | null;
}

const DoctorRecord: React.FC<DoctorRecordProps> = ({ embeddedId, embedded = false, prefill }) => {
  const params = useParams();
  const id = embeddedId != null ? String(embeddedId) : params.id;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // === ローディング・基本状態 ===
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState<any>(null);

  const [diagnosis, setDiagnosis] = useState('');
  const [prescriptionText, setPrescriptionText] = useState('');
  const [notes, setNotes] = useState('');
  // AI提案の「診療録へ適用」を押したか（採用度フィードバック用）
  const [aiApplied, setAiApplied] = useState(false);

  const [medicineList, setMedicineList] = useState<Medicine[]>([]);
  const [selectedPlans, setSelectedPlans] = useState<SelectedPlan[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [doctorName, setDoctorName] = useState<string>('医師');

  // === 予約一覧へ戻る（以前の検索状態を維持） ===
  const goBackToReservations = useCallback(() => {
    const saved = getSavedSearchForPath('/doctor/reservations');
    navigate({ pathname: '/doctor/reservations', search: saved || '' });
  }, [navigate]);

  // === 医师信息 ===
  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        const res = await api.get('/doctor/me', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const name =
          res.data?.profile?.full_name ||
          res.data?.name ||
          res.data?.email ||
          '医師';
        setDoctorName(name);
      } catch (err) {
        console.error('Failed to fetch doctor info', err);
      }
    };
    fetchDoctor();
  }, []);

  // -- 工具：把任意值解析为 { planId: months }，支持对象/数组/"123:6"/JSON字符串
  const normalizePlanMonths = useCallback((val: any): Record<number, number> => {
    const clamp = (n: any) => {
      const x = Number(n);
      if (!Number.isFinite(x)) return undefined;
      return Math.max(1, Math.min(120, Math.floor(x)));
    };

    const assignIfOk = (out: Record<number, number>, pid: any, months: any) => {
      const k = Number(pid);
      const m = clamp(months);
      if (Number.isFinite(k) && m !== undefined) out[k] = m;
    };

    const out: Record<number, number> = {};
    if (val == null) return out;

    let value = val;

    // 若是 JSON 字符串（看起来以 { 或 [ 开头），先尝试 parse
    if (typeof value === 'string' && (value.trim().startsWith('{') || value.trim().startsWith('['))) {
      try {
        value = JSON.parse(value);
      } catch {
        // 继续尝试 "123:6,124:3" 形式
      }
    }

    // 对象: { "123":6, "124":3 } / {123:6}
    if (typeof value === 'object' && !Array.isArray(value)) {
      for (const k of Object.keys(value)) assignIfOk(out, k, (value as any)[k]);
      return out;
    }

    // 数组: [{ price_plan_id:123, months:6 }, ...]
    if (Array.isArray(value)) {
      for (const it of value) {
        if (it && typeof it === 'object') {
          const pid =
            it.price_plan_id ?? it.plan_id ?? it.planId ?? it.pricePlanId;
          const m = it.months ?? it.month ?? it.count;
          assignIfOk(out, pid, m);
        }
      }
      return out;
    }

    // 字符串: "123:6,124:3"
    if (typeof value === 'string') {
      const parts = value.split(',').map(s => s.trim()).filter(Boolean);
      for (const p of parts) {
        const [a, b] = p.split(':').map(s => s.trim());
        assignIfOk(out, a, b);
      }
      return out;
    }

    return out;
  }, []);

  // === 预约与药品；恢复选择 ===
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');

        // 1) 预约列表里找到目标
        const res = await api.get(`/doctor/reservations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = res.data || [];
        const target = list.find((r: any) => String(r.id) === String(id));
        if (!target) {
          setLoading(false);
          return;
        }
        setReservation(target);
        setDiagnosis(target.diagnosis || '');
        setPrescriptionText(target.prescription_text || '');
        setNotes(target.notes || '');

        // 2) 药品列表（按 reservation_id 过滤）
        let meds: Medicine[] = [];
        try {
          const medRes = await api.get(`/doctor/medicines`, {
            params: { reservation_id: id },
            headers: { Authorization: `Bearer ${token}` },
          });
          meds = medRes.data || [];
        } catch {
          try {
            const medRes2 = await api.get(`/doctor/medicines/${id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            meds = medRes2.data || [];
          } catch (e2) {
            console.error('Failed to fetch medicines', e2);
            meds = [];
          }
        }
        setMedicineList(meds);

        // 3) 恢复已选 plan 与 months
        const selectedPlanIds: number[] = target.selected_price_plan_ids || [];

        const restoredMonths: Record<number, number> =
          normalizePlanMonths(
            target.selected_plan_months || target.selected_price_plan_months
          );

        if (selectedPlanIds.length > 0) {
          const mapped: SelectedPlan[] = selectedPlanIds
            .map((pid) => {
              const med = meds.find((m) => m.price_plans.some((p) => p.id === pid));
              const plan = med?.price_plans.find((p) => p.id === pid);
              if (!med || !plan) return null;

              const months =
                plan.plan_type === 'subscription' && plan.duration_months > 0
                  ? plan.duration_months
                  : (restoredMonths[pid] ?? 1);

              return { medicineId: med.id, pricePlanId: pid, months };
            })
            .filter(Boolean) as SelectedPlan[];
          setSelectedPlans(mapped);
        } else {
          // 旧数据：只有 medicine_ids → 取第一个 plan；订阅=duration，单品=1
          const legacyMedIds: number[] = Array.isArray(target.medicine_ids)
            ? target.medicine_ids
            : `${target.medicine_ids || ''}`
              .split(',')
              .map((s: string) => parseInt(s.trim(), 10))
              .filter((n: number) => Number.isFinite(n));

          const mapped: SelectedPlan[] = legacyMedIds
            .map((mid) => {
              const med = meds.find((m) => m.id === mid);
              const plan = med?.price_plans?.[0];
              if (!med || !plan) return null;
              const months =
                plan.plan_type === 'subscription' && plan.duration_months > 0
                  ? plan.duration_months
                  : 1;
              return { medicineId: mid, pricePlanId: plan.id, months };
            })
            .filter(Boolean) as SelectedPlan[];
          setSelectedPlans(mapped);
        }
      } catch (err) {
        console.error('Failed to fetch data', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, normalizePlanMonths]);

  // === ユーティリティ：薬とプランを同時に取得 ===
  const findMedAndPlan = useCallback(
    (medicineId: number, pricePlanId: number) => {
      const med = medicineList.find((m) => m.id === medicineId);
      const plan = med?.price_plans.find((p) => p.id === pricePlanId);
      return { med, plan };
    },
    [medicineList]
  );

  // === 勾选药：未选→选中（默认：订阅=duration，单品=1） ===
  const handleMedicineToggle = (medicineId: number) => {
    const exists = selectedPlans.find((m) => m.medicineId === medicineId);
    if (exists) {
      setSelectedPlans((prev) => prev.filter((m) => m.medicineId !== medicineId));
      return;
    }
    const med = medicineList.find((m) => m.id === medicineId);
    const firstPlan = med?.price_plans?.[0];
    if (firstPlan) {
      const months =
        firstPlan.plan_type === 'subscription' && firstPlan.duration_months > 0
          ? firstPlan.duration_months
          : 1;
      setSelectedPlans((prev) => [...prev, { medicineId, pricePlanId: firstPlan.id, months }]);
    }
  };

  // === 切换套餐（订阅→固定；单品→保留月数） ===
  const handlePlanChange = (medicineId: number, planId: number) => {
    const med = medicineList.find((m) => m.id === medicineId);
    const plan = med?.price_plans.find((p) => p.id === planId);

    setSelectedPlans((prev) =>
      prev.map((m) => {
        if (m.medicineId !== medicineId) return m;
        if (!plan) return m;

        if (plan.plan_type === 'subscription' && plan.duration_months > 0) {
          return { ...m, pricePlanId: planId, months: plan.duration_months };
        }
        const safeMonths = Math.max(1, Math.min(120, Math.floor(m.months || 1)));
        return { ...m, pricePlanId: planId, months: safeMonths };
      })
    );
  };

  // === 修改月数（仅单品；订阅锁定） ===
  const handleMonthsChange = (medicineId: number, months: number) => {
    const med = medicineList.find((m) => m.id === medicineId);
    if (!med) return;
    const sel = selectedPlans.find((sp) => sp.medicineId === medicineId);
    const plan = med.price_plans.find((p) => p.id === sel?.pricePlanId);
    if (!plan) return;

    if (plan.plan_type === 'subscription' && plan.duration_months > 0) return;

    const safe = Math.max(1, Math.min(120, Math.floor(months || 1)));
    setSelectedPlans((prev) =>
      prev.map((m) => (m.medicineId === medicineId ? { ...m, months: safe } : m))
    );
  };

  // === プラン表示ラベル ===
  const formatPlanLabel = (p: PricePlan) => {
    if (p.plan_type === 'subscription' && (p.duration_months || 0) > 0) {
      if (p.duration_months === 1) {
        return (t('admin.medicine.subscription_1m') as string) || '定期 1ヶ月';
      }
      return `定期 ${p.duration_months}ヶ月`;
    }
    return (t('admin.medicine.single_purchase') as string) || '単品購入';
  };

  // === 处方文案 ===
  const updatePrescriptionText = useCallback(() => {
    const lines = selectedPlans.map((sp) => {
      const { med, plan } = findMedAndPlan(sp.medicineId, sp.pricePlanId);
      if (!med || !plan) return '';

      const note = med.note || (t('doctor.no_note') as string) || '備考なし';
      const unitPrice = Number(plan.price_tax_included) || 0;

      const translatedName = translateMedicineName(med.name, i18n.language);
      if (plan.plan_type === 'subscription' && plan.duration_months > 0) {
        const months = plan.duration_months;
        const total = unitPrice * months;
        return `${translatedName}（${note}）月額 ${unitPrice.toLocaleString('ja-JP')}円 × ${months}ヶ月 = ${total.toLocaleString('ja-JP')}円（税込）`;
      }

      const months = sp.months || 1;
      const total = unitPrice * months;
      // Check billing_unit to determine display unit
      const unit = plan.billing_unit === 'month' ? 'ヶ月' : '錠';
      return `${translatedName}（${note}）単価 ${unitPrice.toLocaleString('ja-JP')}円 × ${months}${unit} = ${total.toLocaleString('ja-JP')}円（税込）`;
    });

    setPrescriptionText(lines.filter(Boolean).join('\n'));
  }, [selectedPlans, findMedAndPlan, t]);

  // === 分离配送费和其他药品 ===
  const shippingMedicines = medicineList.filter((m) => m.service_type === 'shipping');
  const regularMedicines = medicineList.filter((m) => m.service_type !== 'shipping');

  // === 配送选项状态 ===
  const [selectedShippingId, setSelectedShippingId] = useState<number | null>(null);

  // 初始化配送选项 (如果已选则恢复)
  useEffect(() => {
    if (shippingMedicines.length > 0 && selectedPlans.length > 0) {
      // 查找 selectedPlans 中是否有属于 shippingMedicines 的 plan
      for (const sm of shippingMedicines) {
        for (const p of sm.price_plans) {
          if (selectedPlans.some(sp => sp.pricePlanId === p.id)) {
            setSelectedShippingId(p.id);
            return;
          }
        }
      }
    }
  }, [shippingMedicines, selectedPlans]);

  // AI提案が届いたら diagnosis(AP) と notes(S) に反映
  // 空の項目では既存入力を上書きしない(AI未使用モードは AP が空のため診断を消さない)
  // 「適用」されたことを記録し、保存時に採用度フィードバックへ送る
  useEffect(() => {
    if (!prefill) return;
    if (prefill.ap) setDiagnosis(prefill.ap);
    if (prefill.s) setNotes(prefill.s);
    setAiApplied(true);
  }, [prefill]);

  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // === 保存（关键：selected_plan_months & 兼容字段 selected_price_plan_months） ===
  const handleSave = async (opts?: { thenComplete?: boolean }) => {
    // 验证配送费
    if (shippingMedicines.length > 0 && !selectedShippingId) {
      alert(t('doctor.select_shipping') || '配送料を選択してください。');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');

      // 合并常规药品和配送费
      const finalSelectedPlans = [...selectedPlans.filter(sp => {
        // 过滤掉旧的 shipping plan 以防重复添加
        const med = medicineList.find(m => m.id === sp.medicineId);
        return med && med.service_type !== 'shipping';
      })];

      // 添加选中的配送费
      if (selectedShippingId) {
        const shipMed = shippingMedicines.find(m => m.price_plans.some(p => p.id === selectedShippingId));
        if (shipMed) {
          finalSelectedPlans.push({
            medicineId: shipMed.id,
            pricePlanId: selectedShippingId,
            months: 1 // 固定数量 1
          });
        }
      }

      const selected_price_plan_ids = finalSelectedPlans.map((x) => x.pricePlanId);
      const medicine_ids = finalSelectedPlans.map((x) => x.medicineId);

      const selected_plan_months: Record<number, number> = {};
      for (const sp of finalSelectedPlans) {
        const { plan } = findMedAndPlan(sp.medicineId, sp.pricePlanId);
        if (!plan) continue;
        const months =
          plan.plan_type === 'subscription' && plan.duration_months > 0
            ? plan.duration_months
            : (sp.months || 1);
        selected_plan_months[sp.pricePlanId] = months;
      }

      // 兼容：有些后端读取 selected_price_plan_months（同语义）
      const selected_price_plan_months = selected_plan_months;

      // 如果 notes 为空，自动填充"なし"
      const finalNotes = (notes || '').trim() || 'なし';

      const payload = {
        diagnosis,
        prescription_text: prescriptionText,
        notes: finalNotes,
        medicine_ids,
        selected_price_plan_ids,
        selected_plan_months,
        selected_price_plan_months, // 兼容字段
        ai_applied: aiApplied,       // AI提案を適用したか（採用度フィードバック）
      };



      await api.post(
        `/doctor/save_medical_record/${id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // 「保存して診療完了」: 続けて診療完了（status=completed + 決済リンク送信）→ 予約一覧へ戻る
      if (opts?.thenComplete) {
        try {
          await api.post(
            '/doctor/complete_visit',
            { reservation_id: Number(id) },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (ce: any) {
          const cd = ce?.response?.data?.detail;
          alert(((t('doctor.complete_after_save_fail') as string) || '診療記録は保存しましたが、診療完了に失敗しました：') + (typeof cd === 'string' ? cd : ''));
          return;
        }
        alert((t('doctor.complete_success') as string) || '診療完了しました。患者様に決済リンクを送信しました。');
        goBackToReservations();
        return;
      }

      alert((t('doctor.mark_success') as string) || '保存しました');
      if (!embedded) goBackToReservations();
    } catch (err: any) {
      console.error('Failed to save record', err);
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (t('doctor.mark_fail') as string) || '保存に失敗しました';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center">{(t('doctor.loading') as string) || '読み込み中…'}</div>;
  }

  const profile = reservation?.user_profile || {};
  const birthday = profile.birth_date ? dayjs(profile.birth_date) : null;
  const age = birthday ? dayjs().diff(birthday, 'year') : (t('doctor.na') as string) || '-';
  const birthdayDisplay = birthday
    ? `${birthday.format('YYYY-MM-DD')}（${age}${(t('doctor.years_old') as string) || '歳'}）`
    : ((t('doctor.na') as string) || '-');

  // === カルテ番号 ===
  const chartNumber =
    reservation?.patient_chart_number ??
    profile?.chart_number ??
    profile?.karte_number ??
    profile?.medical_record_number ??
    profile?.karteNo ??
    profile?.mrn ??
    profile?.patient_code ??
    profile?.patient_id ??
    null;

  // 埋め込みモードはラッパー・ヘッダー不要
  const Inner = (
    <div className={embedded ? 'p-4' : 'bg-white shadow border rounded-lg w-full max-w-[794px] p-6'}>
        {/* === ヘッダー（埋め込み時は非表示） === */}
        {!embedded && (
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={goBackToReservations}
            className="px-5 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
          >
            {(t('doctor.back') as string) || '戻る'}
          </button>
          <h1 className="text-2xl font-bold">{(t('doctor.record_title') as string) || 'カルテ'}</h1>
          <div className="w-20" />
        </div>
        )}

        {/* === 患者情報 === */}
        <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
        {/* === モバイル（<md）: 患者情報を縦積みカードで表示（横スクロール／見切れ防止） === */}
        <div className="md:hidden divide-y divide-gray-200 text-sm">
          <div className="px-3 py-2 break-words">
            <span className="text-gray-500 font-medium">{(t('doctor.name') as string) || '氏名'}：</span>
            <span>{profile.full_name || reservation?.user_name || ((t('doctor.na') as string) || '-')}</span>
          </div>
          <div className="px-3 py-2 break-words">
            <span className="text-gray-500 font-medium">{(t('doctor.birthday') as string) || '生年月日'}：</span>
            <span>{birthdayDisplay}</span>
          </div>
          <div className="px-3 py-2 break-words">
            <span className="text-gray-500 font-medium">{(t('doctor.furigana') as string) || 'ふりがな'}：</span>
            <span>{profile.furigana || ((t('doctor.na') as string) || '-')}</span>
          </div>
          <div className="px-3 py-2 break-words">
            <span className="text-gray-500 font-medium">{(t('doctor.gender') as string) || '性別'}：</span>
            <span>{profile.gender || ((t('doctor.na') as string) || '-')}</span>
          </div>
          <div className="px-3 py-2 break-words">
            <span className="text-gray-500 font-medium">{(t('doctor.chart_number') as string) || 'カルテ番号'}：</span>
            <span>{chartNumber || ((t('doctor.na') as string) || '-')}</span>
          </div>
          <div className="px-3 py-2 break-words">
            <span className="text-gray-500 font-medium">{(t('doctor.service') as string) || '診療サービス'}：</span>
            <span>{t(`service.${reservation?.service}`) || reservation?.service || '-'}</span>
          </div>
        </div>
        {/* === デスクトップ（md 以上）: 従来のテーブル表示 === */}
        <table className="hidden md:table w-full text-sm border-collapse">
          <tbody className="[&_td]:border [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-2">
            <tr>
              <td className="w-1/5 bg-gray-50 text-gray-500 font-medium">{(t('doctor.name') as string) || '氏名'}</td>
              <td>{profile.full_name || reservation?.user_name || ((t('doctor.na') as string) || '-')}</td>
              <td className="w-1/5 bg-gray-50 text-gray-500 font-medium">{(t('doctor.birthday') as string) || '生年月日'}</td>
              <td>{birthdayDisplay}</td>
            </tr>
            <tr>
              <td className="bg-gray-50 text-gray-500 font-medium">{(t('doctor.furigana') as string) || 'ふりがな'}</td>
              <td>{profile.furigana || ((t('doctor.na') as string) || '-')}</td>
              <td className="bg-gray-50 text-gray-500 font-medium">{(t('doctor.gender') as string) || '性別'}</td>
              <td>{profile.gender || ((t('doctor.na') as string) || '-')}</td>
            </tr>
            {/* === カルテ番号・診療サービス === */}
            <tr>
              <td className="bg-gray-50 text-gray-500 font-medium">{(t('doctor.chart_number') as string) || 'カルテ番号'}</td>
              <td>{chartNumber || ((t('doctor.na') as string) || '-')}</td>
              <td className="bg-gray-50 text-gray-500 font-medium">{(t('doctor.service') as string) || '診療サービス'}</td>
              <td>
                {t(`service.${reservation?.service}`) || reservation?.service || '-'}
              </td>
            </tr>
          </tbody>
        </table>
        </div>

        {/* === 记录表单 === */}
        <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
        <table className="w-full text-sm border-collapse">
          <tbody className="[&_td]:border [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-2">
            <tr>
              <td className="w-1/5 align-top bg-gray-50 text-gray-500 font-medium">{(t('doctor.diagnosis') as string) || '診断'}</td>
              <td>
                <textarea
                  value={diagnosis}
                  onChange={(e) => setDiagnosis(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg p-2.5 focus:outline-none focus:border-coral focus:ring-1 focus:ring-coral/20 transition"
                  rows={3}
                />
              </td>
            </tr>
            <tr>
              <td className="align-top bg-gray-50 text-gray-500 font-medium">{(t('doctor.prescription') as string) || '処方'}</td>
              <td>
                <div className="relative">
                  <textarea
                    value={prescriptionText || ''}
                    readOnly
                    className="w-full border border-gray-200 rounded-lg p-2.5 bg-gray-50 text-gray-700"
                    rows={3}
                  />
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="absolute top-2 right-2 px-4 py-1.5 bg-white text-coral text-xs md:text-sm border border-coral/30 rounded-full font-bold shadow-soft hover:bg-coral/5 transition-all active:scale-95"
                  >
                    {prescriptionText?.trim()
                      ? ((t('doctor.edit_medicine') as string) || '薬を編集')
                      : ((t('doctor.select_medicine') as string) || '薬を選択')}
                  </button>
                </div>
              </td>
            </tr>
            <tr>
              <td className="align-top bg-gray-50 text-gray-500 font-medium">{(t('doctor.notes') as string) || 'メモ'}</td>
              <td>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg p-2.5 focus:outline-none focus:border-coral focus:ring-1 focus:ring-coral/20 transition"
                  rows={3}
                />
              </td>
            </tr>
          </tbody>
        </table>
        </div>

        {/* === 配送オプション（必須） === */}
        {shippingMedicines.length > 0 && (
          <div className="mb-6 border p-4 rounded-lg bg-gray-50">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <span className="text-red-500">*</span>
              {t('doctor.shipping_fee') || '配送料 (Shipping)'}
            </h3>
            <div className="space-y-2">
              {shippingMedicines.flatMap(m => m.price_plans.map(p => ({ medicine: m, plan: p }))).map(({ medicine, plan }) => (
                <label key={plan.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="shipping_plan"
                    checked={selectedShippingId === plan.id}
                    onChange={() => setSelectedShippingId(plan.id)}
                  />
                  <span>
                    {translateMedicineName(medicine.name, i18n.language)}
                    {plan.price_tax_included > 0 && ` (+${plan.price_tax_included.toLocaleString()}円)`}
                  </span>
                </label>
              ))}
            </div>
            {!selectedShippingId && (
              <p className="text-red-500 text-xs mt-1">
                {t('doctor.shipping_required') || '保存前に必ず選択してください。'}
              </p>
            )}
          </div>
        )}

        {/* === 署名行 === */}
        <div className="flex justify-between items-center text-sm mb-4">
          <div>{(t('doctor.doctor_sign') as string) || '医師署名'}: {doctorName} 医師</div>
          <div>Melodis Clinic</div>
        </div>

        {/* === 保存 === */}
        {(() => {
          const isPaid = reservation?.payment_status === 'paid';
          const isDisabled = isPaid || (shippingMedicines.length > 0 && !selectedShippingId);
          return (
            <div className="flex justify-end items-center gap-3">
              {isPaid && (
                <span className="text-xs text-gray-400">
                  {(t('doctor.record_locked') as string) || '支払い完了済みのため編集不可'}
                </span>
              )}
              <button
                onClick={() => handleSave()}
                disabled={isDisabled || submitting}
                title={isPaid ? ((t('doctor.record_locked') as string) || '支払い完了済みのため編集不可') : undefined}
                className={`px-6 py-2 text-white rounded-lg ${isDisabled
                  ? 'bg-gray-400 cursor-not-allowed opacity-50'
                  : 'bg-coral hover:bg-coral'
                  }`}
              >
                {(t('doctor.save') as string) || '保存'}
              </button>
              {/* 三屏問診画面のみ: 保存 + 診療完了 を1クリックで（医師が診療完了の押し忘れで決済不能になるのを防ぐ）。完了済みには出さない */}
              {embedded && reservation?.status !== 'completed' && (
                <button
                  onClick={() => setShowCompleteConfirm(true)}
                  disabled={isDisabled || submitting}
                  title={isPaid ? ((t('doctor.record_locked') as string) || '支払い完了済みのため編集不可') : undefined}
                  className={`px-6 py-2 text-white rounded-lg font-bold ${isDisabled
                    ? 'bg-gray-400 cursor-not-allowed opacity-50'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                    }`}
                >
                  {(t('doctor.save_and_complete') as string) || '保存して診療完了'}
                </button>
              )}
            </div>
          );
        })()}

        {/* 診療完了の確認: 内容(診断/処方)を再確認してから status変更 + 決済リンク送信 */}
        {showCompleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-in zoom-in-95 duration-200">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {(t('doctor.complete_confirm_title') as string) || '診療完了の確認'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {(t('doctor.complete_confirm_desc') as string) || '診療完了にすると、患者様に決済リンクが送信されます。以下の内容に誤りがないかご確認ください。'}
              </p>
              <div className="bg-gray-50 border rounded-xl p-3 mb-5 space-y-3 text-sm max-h-56 overflow-y-auto">
                <div>
                  <div className="text-xs font-bold text-gray-400 mb-0.5">{(t('doctor.diagnosis') as string) || '診断'}</div>
                  <div className={diagnosis && diagnosis.trim() ? 'text-gray-800 whitespace-pre-wrap' : 'text-red-500 font-bold'}>
                    {(diagnosis && diagnosis.trim()) || ((t('doctor.not_filled') as string) || '（未入力）')}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-bold text-gray-400 mb-0.5">{(t('doctor.prescription') as string) || '処方'}</div>
                  <div className={prescriptionText && prescriptionText.trim() ? 'text-gray-800 whitespace-pre-wrap' : 'text-red-500 font-bold'}>
                    {(prescriptionText && prescriptionText.trim()) || ((t('doctor.not_filled') as string) || '（未入力）')}
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCompleteConfirm(false)}
                  className="flex-1 px-4 py-3 border rounded-xl text-gray-600 font-bold hover:bg-gray-50 transition"
                >
                  {(t('common.cancel') as string) || 'キャンセル'}
                </button>
                <button
                  onClick={() => { setShowCompleteConfirm(false); handleSave({ thenComplete: true }); }}
                  className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition shadow-lg shadow-emerald-600/20"
                >
                  {(t('doctor.complete_confirm_ok') as string) || '診療完了にする'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* === 薬選択モーダル（订阅锁定月数 / 单品可输入月数） === */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-md w-[520px] max-w-full max-h-[80vh] overflow-y-auto">
              <h2 className="text-lg font-semibold mb-4">
                {(t('doctor.select_medicine') as string) || '処方する薬を選択'}
              </h2>

              <div className="space-y-4">
                {regularMedicines.length > 0 ? (
                  regularMedicines.map((med) => {
                    const selected = selectedPlans.find((m) => m.medicineId === med.id);
                    const selectedPlanId = selected?.pricePlanId ?? med.price_plans?.[0]?.id;

                    const currentPlan =
                      med.price_plans.find((p) => p.id === selectedPlanId) || med.price_plans[0];
                    const unitPrice = Number(currentPlan?.price_tax_included || 0);
                    const isSubscription =
                      currentPlan?.plan_type === 'subscription' && (currentPlan?.duration_months || 0) > 0;

                    const months = isSubscription ? currentPlan!.duration_months : (selected?.months ?? 1);
                    const subtotal = (unitPrice * months).toLocaleString('ja-JP');

                    return (
                      <div key={med.id} className="border p-3 rounded-lg">
                        <label className="flex items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            checked={!!selected}
                            onChange={() => handleMedicineToggle(med.id)}
                          />
                          <span className="font-medium">
                            {translateMedicineName(med.name, i18n.language)}（{med.note || (t('doctor.no_note') as string) || '備考なし'}）
                          </span>
                        </label>

                        {!!selected && med.price_plans?.length > 0 && (
                          <>
                            {/* 套餐下拉：订阅条目会显示“定期 Xヶ月”，单品为“単品購入” */}
                            <select
                              className="w-full border rounded-lg p-1"
                              value={selectedPlanId}
                              onChange={(e) => handlePlanChange(med.id, Number(e.target.value))}
                            >
                              {med.price_plans.map((plan) => {
                                const priceStr = plan.price_tax_included.toLocaleString('ja-JP');
                                const label = formatPlanLabel(plan);
                                const suffix = plan.plan_type === 'subscription' && (plan.duration_months || 0) > 0 ? '（月額）' : '（単価）';
                                return (
                                  <option key={plan.id} value={plan.id}>
                                    {label}{suffix}：{priceStr}円
                                  </option>
                                );
                              })}
                            </select>

                            {/* 价格与数量/总价区域 */}
                            <div className="mt-2 text-sm">
                              {isSubscription ? (
                                <>
                                  <div>
                                    {(t('doctor.monthly_price') as string) || '月額'}：{unitPrice.toLocaleString('ja-JP')}円
                                  </div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    （{(t('doctor.monthly_price') as string) || '月額'} {unitPrice.toLocaleString('ja-JP')}円 × {currentPlan!.duration_months}
                                    {(t('doctor.month') as string) || 'ヶ月'} = {subtotal}円）
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div>
                                    {(t('doctor.unit_price') as string) || '単価'}：{unitPrice.toLocaleString('ja-JP')}円
                                  </div>
                                  {/* 单品：可输入月数或数量 */}
                                  <div className="mt-2">
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="number"
                                        min={1}
                                        max={120}
                                        value={months}
                                        onChange={(e) =>
                                          handleMonthsChange(med.id, Number(e.target.value))
                                        }
                                        className="w-24 border rounded-lg px-2 py-1"
                                      />
                                      <span className="text-sm">
                                        {currentPlan?.billing_unit === 'month' ? 'ヶ月' : '錠'}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                      （{(t('doctor.unit_price') as string) || '単価'} {unitPrice.toLocaleString('ja-JP')}円 × {months}
                                      {currentPlan?.billing_unit === 'month' ? 'ヶ月' : '錠'} = {subtotal}円）
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p>{(t('doctor.no_medicine_available') as string) || '利用可能な薬がありません。'}</p>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
                >
                  {(t('doctor.cancel') as string) || 'キャンセル'}
                </button>
                <button
                  onClick={() => {
                    updatePrescriptionText();
                    setIsModalOpen(false);
                  }}
                  className="px-5 py-2 bg-coral text-white rounded-lg hover:opacity-90 transition text-sm font-medium"
                >
                  {(t('doctor.confirm') as string) || '確認'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );

  return embedded ? Inner : (
    <div className="flex justify-center mt-6">{Inner}</div>
  );
};

export default DoctorRecord;
